import React, { useMemo, useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragMoveEvent,
  DragOverlay,
  useDraggable,
  useDroppable,
  useSensors,
  useSensor,
  PointerSensor,
} from '@dnd-kit/core';
import { Lesson, Term, doTimesOverlap } from '@court-sync/shared';
import { Badge } from '../ui/Badge';
import { Users, Clock, XCircle, Trash2, AlertTriangle, Layers, UserPlus } from 'lucide-react';
import {
  getLessonSlotStylePropsForLesson,
} from '../../utils/lessonSlotColors';
import { useAgeGroupLookups } from '../../queries/ageGroups';
import { formatTimeHHMM, formatTimeForTooltip, formatTime, timeToMinutes, minutesToTime } from '../../utils/timeFormat';
import { formatCurrency } from '../../utils/currency';
import { getLessonDaysOfWeek } from '../../utils/lessonDays';
import type { LessonAvailabilityWarningDays } from '../../utils/coachAvailabilityWarnings';
import { getCoachFromCache } from '../../queries/coaches';
import { coachService } from '../../services/coach';
import {
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
} from '../ui/Tooltip';
import { earliestActiveLessonStartMinutes, scheduleScrollTopForAnchor } from '../../utils/scheduleScroll';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Match academy weekly calendar: narrow time gutter, wider day columns. */
const WEEKLY_GRID_CLASS = 'grid grid-cols-[2.75rem_repeat(7,minmax(6.75rem,1fr))]';
const WEEKLY_PX_PER_30_MIN = 50;

export interface TermScheduleViewProps {
  lessons: Lesson[];
  term: Term;
  /** When lessons have `programId`, map to program display names (e.g. from academy programs list). */
  programNameById?: Record<string, string>;
  onLessonClick?: (lesson: Lesson) => void;
  onLessonMove?: (lessonId: string, payload: { dayOfWeek: number; startTime: string; endTime: string }) => Promise<void>;
  onLessonCopy?: (sourceLesson: Lesson, payload: { dayOfWeek: number; startTime: string; endTime: string }) => Promise<void>;
  onTimeSlotClick?: (payload: { dayOfWeek: number; startTime: string; endTime: string }) => void;
  onLessonDelete?: (lesson: Lesson) => void;
  /** Cancel entire term lesson when students are enrolled (delete is unavailable). */
  onLessonCancel?: (lesson: Lesson) => void;
  /** Open staff enroll drawer for this term lesson (group or private). */
  onStaffEnroll?: (lesson: Lesson) => void;
  coachAvailabilityWarnings?: LessonAvailabilityWarningDays;
}

// Draggable lesson slot
function DraggableLessonSlot({
  lesson,
  style,
  isFull,
  getAvailableSpots,
  onClick,
  onDelete,
  onCancel,
  onStaffEnroll,
  programNameById,
  dayLessons: _dayLessons,
  getLessonStyle: _getLessonStyle,
  getCoachName: _getCoachName,
  lessonsOverlap: _lessonsOverlap,
  getOverlappingGroups: _getOverlappingGroups,
  hasCoachAvailabilityWarning = false,
  ageGroupById,
}: {
  lesson: Lesson;
  style: { top: string; height: string; left: string; width: string };
  dayLessons: Lesson[];
  getLessonStyle: (l: Lesson, d: Lesson[]) => { top: string; height: string; left: string; width: string };
  isFull: (l: Lesson) => boolean;
  getAvailableSpots: (l: Lesson) => number;
  getCoachName: (id: string) => string;
  programNameById?: Record<string, string>;
  onClick?: () => void;
  onDelete?: (lesson: Lesson) => void;
  onCancel?: (lesson: Lesson) => void;
  onStaffEnroll?: (lesson: Lesson) => void;
  lessonsOverlap: (a: Lesson, b: Lesson) => boolean;
  getOverlappingGroups: (d: Lesson[]) => Lesson[][];
  hasCoachAvailabilityWarning?: boolean;
  ageGroupById?: Record<string, import('@/utils/lessonSlotColors').AgeGroupCalendarColorLookup>;
}) {
  const isCancelled = !!lesson.cancelledFromDate;
  const hasEnrollments = (lesson.currentEnrollment ?? 0) > 0;
  const showCancelAction = !!onCancel && !isCancelled && hasEnrollments;
  const showDeleteAction = !!onDelete && !hasEnrollments;
  const programName =
    lesson.programId && programNameById?.[lesson.programId] ? programNameById[lesson.programId] : undefined;
  const slotStyleProps =
    !isCancelled && !isFull(lesson)
      ? getLessonSlotStylePropsForLesson(lesson, ageGroupById)
      : null;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lesson.id,
    data: { lesson },
  });

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <div
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          data-lesson-slot="true"
          onClick={() => {
            if (onClick && !isCancelled) onClick();
          }}
          className={`absolute rounded p-1.5 text-xs select-none overflow-hidden flex flex-col min-h-0 min-w-0 leading-tight ${
            isDragging ? 'opacity-50 ring-2 ring-blue-400 ring-offset-2 z-50' : ''
          } ${
            isCancelled
              ? 'border border-amber-300 dark:border-amber-700 border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 opacity-80'
              : isFull(lesson)
                ? 'border border-red-300 dark:border-red-700 border-l-4 border-l-red-500 bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-100'
                : slotStyleProps?.className ?? ''
          } ${onClick && !isCancelled ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
          style={{
            top: style.top,
            height: style.height,
            left: style.left,
            width: style.width,
            ...slotStyleProps?.style,
          }}
        >
          <div className="font-medium mb-0.5 flex items-center gap-1.5 min-w-0 shrink-0">
            {isCancelled && <XCircle className="h-3 w-3 flex-shrink-0 text-amber-600 dark:text-amber-400" />}
            {hasCoachAvailabilityWarning && (
              <AlertTriangle className="h-3 w-3 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            )}
            <span className="min-w-0 truncate">{lesson.name}</span>
          </div>
          <div className="text-xs opacity-75 truncate min-w-0 shrink-0">
            {formatTimeHHMM(lesson.startTime)} - {formatTimeHHMM(lesson.endTime)}
          </div>
          {programName && (
            <div className="flex items-center gap-1 mt-0.5 min-w-0 shrink-0 text-[10px] leading-tight text-violet-700 dark:text-violet-300/90">
              <Layers className="h-2.5 w-2.5 flex-shrink-0" aria-hidden />
              <span className="min-w-0 truncate" title={programName}>
                {programName}
              </span>
            </div>
          )}
          {lesson.maxCapacity !== -1 && (
            <div className="flex items-center gap-1 mt-0.5 min-w-0 shrink-0">
              <Users className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="text-xs min-w-0 flex-1 truncate">
                {lesson.currentEnrollment || 0}/{lesson.maxCapacity}
              </span>
              {isFull(lesson) ? (
                <Badge
                  variant="secondary"
                  className="bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-100 text-xs px-1 py-0 ml-auto flex-shrink-0"
                >
                  Full
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-100 text-xs px-1 py-0 ml-auto flex-shrink-0"
                >
                  {getAvailableSpots(lesson)}
                </Badge>
              )}
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8} avoidCollisions collisionPadding={8} className="w-64 max-w-[min(256px,90vw)] p-3 text-sm text-white bg-gray-900 dark:bg-gray-800 rounded-lg shadow-xl border-0">
        <div className="space-y-2">
          <div>
            <h3 className="font-semibold text-base mb-1 flex items-center gap-2">{lesson.name}</h3>
            {lesson.description && <p className="text-xs text-gray-300 dark:text-gray-400 line-clamp-2">{lesson.description}</p>}
          </div>
          <div className="space-y-1.5 pt-1 border-t border-gray-700 dark:border-gray-600">
            <div className="flex justify-between items-center">
              <span className="text-gray-400 dark:text-gray-500">Time:</span>
              <span className="font-medium">{formatTimeForTooltip(lesson.startTime)} - {formatTimeForTooltip(lesson.endTime)}</span>
            </div>
            {programName && (
              <div className="flex justify-between items-start gap-2">
                <span className="text-gray-400 dark:text-gray-500 shrink-0">Program:</span>
                <span className="font-medium text-right">{programName}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-400 dark:text-gray-500">Price:</span>
              <span className="font-medium">{formatCurrency(lesson.price, lesson.currency || 'AUD')}</span>
            </div>
          </div>
          {hasCoachAvailabilityWarning && (
            <div className="mt-2 pt-2 border-t border-amber-600/30 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-400 mt-0.5" />
              <p className="text-xs text-amber-100">Coach is not available during this time slot</p>
            </div>
          )}
          <p className="text-xs text-gray-400 pt-1">Drag to move • Hold Alt/Ctrl to copy</p>
          {onStaffEnroll && !isCancelled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStaffEnroll(lesson);
              }}
              className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 px-2 text-xs font-medium text-gray-900 bg-white hover:bg-gray-100 rounded transition-colors"
              aria-label={`Manage students for ${lesson.name}`}
            >
              <UserPlus className="h-3.5 w-3.5" />
              Manage students
            </button>
          )}
          {showCancelAction && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCancel!(lesson);
              }}
              className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 px-2 text-xs font-medium text-amber-100 hover:text-white hover:bg-amber-900/50 rounded transition-colors border border-amber-700/60"
              aria-label={`Cancel lesson ${lesson.name}`}
            >
              <XCircle className="h-3.5 w-3.5" />
              Cancel lesson
            </button>
          )}
          {showDeleteAction && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete!(lesson);
              }}
              className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 px-2 text-xs font-medium text-red-200 hover:text-red-100 hover:bg-red-900/40 rounded transition-colors border border-red-800/50"
              aria-label={`Delete lesson ${lesson.name}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete lesson
            </button>
          )}
        </div>
      </TooltipContent>
    </TooltipRoot>
  );
}

// Drop overlay placeholder shown when dragging
function DragOverlayLesson({
  lesson,
  programNameById,
  ageGroupById,
}: {
  lesson: Lesson;
  programNameById?: Record<string, string>;
  ageGroupById?: Record<string, import('@/utils/lessonSlotColors').AgeGroupCalendarColorLookup>;
}) {
  const programName =
    lesson.programId && programNameById?.[lesson.programId] ? programNameById[lesson.programId] : undefined;
  const slotStyleProps = getLessonSlotStylePropsForLesson(lesson, ageGroupById);
  return (
    <div
      className={`rounded p-1.5 text-xs shadow-xl border-2 border-blue-400 bg-white dark:bg-gray-800 ${slotStyleProps.className}`}
      style={{ minWidth: 140, minHeight: 60, ...slotStyleProps.style }}
    >
      <div className="font-medium truncate">{lesson.name}</div>
      <div className="text-xs opacity-75">{formatTimeHHMM(lesson.startTime)} - {formatTimeHHMM(lesson.endTime)}</div>
      {programName && (
        <div className="flex items-center gap-1 mt-0.5 text-[10px] text-violet-700 dark:text-violet-300/90 truncate">
          <Layers className="h-2.5 w-2.5 flex-shrink-0" aria-hidden />
          <span className="truncate">{programName}</span>
        </div>
      )}
    </div>
  );
}

export default function TermScheduleView({
  lessons,
  term,
  programNameById,
  onLessonClick,
  onLessonMove,
  onLessonCopy,
  onTimeSlotClick,
  onLessonDelete,
  onLessonCancel,
  onStaffEnroll,
  coachAvailabilityWarnings = new Map(),
}: TermScheduleViewProps) {
  const [coachNamesFromPublic, setCoachNamesFromPublic] = useState<Record<string, string>>({});
  const academyId = lessons[0]?.academyId;
  const { ageGroupById } = useAgeGroupLookups(academyId);
  const getCoachById = getCoachFromCache;
  const copyModeRef = useRef(false);
  const scheduleScrollRef = useRef<HTMLDivElement>(null);
  const lastDropYRef = useRef<{ centerY: number; overRect: { top: number; height: number } } | null>(null);
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
  const [dropTarget, setDropTarget] = useState<{ dayIndex: number; topPercent: number; heightPercent: number } | null>(null);
  const [hoverTarget, setHoverTarget] = useState<{ dayIndex: number; topPercent: number; heightPercent: number } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    const academyId = lessons[0]?.academyId;
    if (!academyId) return;
    coachService.getPublicCoaches(academyId)
      .then((coaches) => {
        const map: Record<string, string> = {};
        coaches.forEach((c) => { map[c.id] = `${c.firstName} ${c.lastName}`; });
        setCoachNamesFromPublic(map);
      })
      .catch(() => {});
  }, [lessons]);

  const getCoachName = useCallback((coachId: string): string => {
    const coach = getCoachById(coachId);
    if (coach) return `${coach.firstName} ${coach.lastName}`;
    return coachNamesFromPublic[coachId] || 'Unknown Coach';
  }, [getCoachById, coachNamesFromPublic]);

  // Full day; vertical scroll keeps the layout compact (replaces “Show 24 hours” toggle).
  const { earliestTime, latestTime } = useMemo(
    () => ({ earliestTime: 0, latestTime: 24 * 60 }),
    []
  );

  const lessonAnchorMinutes = useMemo(
    () => earliestActiveLessonStartMinutes(lessons, null),
    [lessons]
  );

  useLayoutEffect(() => {
    const el = scheduleScrollRef.current;
    if (!el) return;
    if (lessonAnchorMinutes === null) {
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = scheduleScrollTopForAnchor(el, lessonAnchorMinutes, earliestTime, latestTime, {
      pxPerSlot: WEEKLY_PX_PER_30_MIN,
    });
  }, [lessonAnchorMinutes, earliestTime, latestTime, term.id]);

  const timeSlots = useMemo(() => {
    const s: number[] = [];
    for (let m = 0; m < 24 * 60; m += 30) s.push(m);
    return s;
  }, []);

  const visibleTimeSlots = useMemo(() =>
    timeSlots.filter((s) => s >= earliestTime && s <= latestTime),
    [timeSlots, earliestTime, latestTime]
  );

  const lessonsByDay = useMemo(() => {
    const g: Record<number, Lesson[]> = {};
    for (let i = 0; i < 7; i++) g[i] = [];
    lessons.forEach((l) => {
      if (l.isActive) {
        const days = getLessonDaysOfWeek(l);
        days.forEach((day) => g[day].push(l));
      }
    });
    return g;
  }, [lessons]);

  const lessonsOverlap = useCallback((a: Lesson, b: Lesson) =>
    doTimesOverlap(a.startTime, a.endTime, b.startTime, b.endTime),
    []
  );

  const getOverlappingGroups = useCallback((dayLessons: Lesson[]): Lesson[][] => {
    if (dayLessons.length === 0) return [];
    const groups: Lesson[][] = [];
    const processed = new Set<string>();
    dayLessons.forEach((lesson) => {
      if (processed.has(lesson.id)) return;
      const group: Lesson[] = [lesson];
      processed.add(lesson.id);
      let foundNew = true;
      while (foundNew) {
        foundNew = false;
        dayLessons.forEach((other) => {
          if (processed.has(other.id)) return;
          if (group.some((g) => lessonsOverlap(g, other))) {
            group.push(other);
            processed.add(other.id);
            foundNew = true;
          }
        });
      }
      groups.push(group);
    });
    return groups;
  }, [lessonsOverlap]);

  const getLessonStyle = useCallback((lesson: Lesson, dayLessons: Lesson[]): { top: string; height: string; left: string; width: string } => {
    const totalRange = latestTime - earliestTime;
    const startMinutes = timeToMinutes(lesson.startTime);
    const endMinutes = timeToMinutes(lesson.endTime);
    const topPercent = ((startMinutes - earliestTime) / totalRange) * 100;
    const heightPercent = ((endMinutes - startMinutes) / totalRange) * 100;
    const groups = getOverlappingGroups(dayLessons);
    const group = groups.find((g) => g.some((l) => l.id === lesson.id));
    if (group && group.length > 1) {
      const idx = group.findIndex((l) => l.id === lesson.id);
      const w = (100 - 2 - 0.5 * (group.length - 1)) / group.length;
      const left = 1 + idx * (w + 0.5);
      return { top: `${topPercent}%`, height: `${heightPercent}%`, left: `${left}%`, width: `${w}%` };
    }
    return { top: `${topPercent}%`, height: `${heightPercent}%`, left: '4px', width: 'calc(100% - 8px)' };
  }, [earliestTime, latestTime, getOverlappingGroups]);

  const isFull = useCallback((l: Lesson) => (l.maxCapacity !== -1) && ((l.currentEnrollment || 0) >= l.maxCapacity), []);
  const getAvailableSpots = useCallback((l: Lesson) => (l.maxCapacity === -1 ? -1 : Math.max(0, l.maxCapacity - (l.currentEnrollment || 0))), []);

  const dayOrder = [1, 2, 3, 4, 5, 6, 0];

  const handleDragStart = useCallback((event: DragStartEvent) => {
    lastDropYRef.current = null;
    setDropTarget(null);
    setHoverTarget(null);
    const ev = event.activatorEvent as { altKey?: boolean; ctrlKey?: boolean };
    copyModeRef.current = ev?.altKey || ev?.ctrlKey || false;
    const lesson = event.active.data.current?.lesson as Lesson | undefined;
    if (lesson) setActiveLesson(lesson);
  }, []);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { over, active } = event;
    if (!over || !over.rect) {
      setDropTarget(null);
      return;
    }
    const translated = (active as unknown as { rect?: { current?: { translated?: { top: number; height: number } } } }).rect?.current?.translated;
    if (!translated) return;
    const centerY = translated.top + translated.height / 2;
    const overRect = { top: over.rect.top, height: over.rect.height };
    lastDropYRef.current = { centerY, overRect };
    const overId = String(over.id);
    if (!overId.startsWith('day-')) return;
    const dayIndex = parseInt(overId.replace('day-', ''), 10);
    const relativeY = centerY - overRect.top;
    const totalRange = latestTime - earliestTime;
    const dropMinutes = earliestTime + (relativeY / overRect.height) * totalRange;
    const snappedMinutes = Math.max(0, Math.min(24 * 60 - 30, Math.round(dropMinutes / 30) * 30));
    const activeLessonData = active.data.current?.lesson as Lesson | undefined;
    const duration = activeLessonData
      ? timeToMinutes(activeLessonData.endTime) - timeToMinutes(activeLessonData.startTime)
      : 60;
    const durationMinutes = Math.min(duration, 24 * 60 - snappedMinutes);
    const topPercent = ((snappedMinutes - earliestTime) / totalRange) * 100;
    const heightPercent = (durationMinutes / totalRange) * 100;
    setDropTarget({ dayIndex, topPercent, heightPercent });
  }, [earliestTime, latestTime]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveLesson(null);
    setDropTarget(null);
    const { active, over } = event;
    if (!over || !active?.data?.current?.lesson) return;

    const lesson = active.data.current.lesson as Lesson;
    const overId = String(over.id);
    if (!overId.startsWith('day-')) return;

    const dayOfWeek = parseInt(overId.replace('day-', ''), 10);
    const cached = lastDropYRef.current;
    lastDropYRef.current = null;

    const droppableRect = over.rect ? { top: over.rect.top, height: over.rect.height } : cached?.overRect;
    if (!droppableRect) return;

    const centerY = cached?.centerY ?? droppableRect.top + droppableRect.height / 2;
    const relativeY = centerY - droppableRect.top;
    const totalRange = latestTime - earliestTime;
    const dropMinutes = earliestTime + (relativeY / droppableRect.height) * totalRange;
    const snappedMinutes = Math.max(0, Math.min(24 * 60 - 30, Math.round(dropMinutes / 30) * 30));

    const duration = timeToMinutes(lesson.endTime) - timeToMinutes(lesson.startTime);
    let endMinutes = snappedMinutes + duration;
    if (endMinutes > 24 * 60) endMinutes = 24 * 60;
    const startTime = minutesToTime(snappedMinutes);
    const endTime = minutesToTime(endMinutes);

    const payload = { dayOfWeek, startTime, endTime };

    if (copyModeRef.current && onLessonCopy) {
      await onLessonCopy(lesson, payload);
    } else if (!copyModeRef.current && onLessonMove) {
      await onLessonMove(lesson.id, payload);
    }
  }, [latestTime, earliestTime, onLessonMove, onLessonCopy]);

  const getSnappedSlotStartMinutes = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.height <= 0) return null;
    const relativeY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
    const totalRange = latestTime - earliestTime;
    const minutesFromTop = (relativeY / rect.height) * totalRange;
    const slotSizeMinutes = 30;
    // Use floor so the highlighted slot is the one directly under the cursor.
    const snappedStartMinutes = earliestTime + Math.floor(minutesFromTop / slotSizeMinutes) * slotSizeMinutes;
    return Math.max(0, Math.min((24 * 60) - slotSizeMinutes, snappedStartMinutes));
  }, [earliestTime, latestTime]);

  const handleTimeSlotClick = useCallback((event: React.MouseEvent<HTMLDivElement>, dayOfWeek: number) => {
    if (!onTimeSlotClick) return;
    const snappedStartMinutes = getSnappedSlotStartMinutes(event);
    if (snappedStartMinutes === null) return;
    const defaultDurationMinutes = 60;
    const endMinutes = Math.min(snappedStartMinutes + defaultDurationMinutes, (24 * 60) - 1);

    onTimeSlotClick({
      dayOfWeek,
      startTime: minutesToTime(snappedStartMinutes),
      endTime: minutesToTime(endMinutes),
    });
  }, [getSnappedSlotStartMinutes, onTimeSlotClick]);

  const handleTimeSlotHover = useCallback((event: React.MouseEvent<HTMLDivElement>, dayOfWeek: number) => {
    if (!onTimeSlotClick) return;
    if (activeLesson) return;
    const target = event.target as HTMLElement;
    if (target.closest('[data-lesson-slot="true"]')) {
      setHoverTarget(null);
      return;
    }
    const snappedStartMinutes = getSnappedSlotStartMinutes(event);
    if (snappedStartMinutes === null) return;
    const durationMinutes = 60;
    const totalRange = latestTime - earliestTime;
    const topPercent = ((snappedStartMinutes - earliestTime) / totalRange) * 100;
    const heightPercent = (durationMinutes / totalRange) * 100;

    setHoverTarget({ dayIndex: dayOfWeek, topPercent, heightPercent });
  }, [activeLesson, earliestTime, latestTime, onTimeSlotClick, getSnappedSlotStartMinutes]);

  const handleTimeSlotHoverLeave = useCallback(() => {
    setHoverTarget(null);
  }, []);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="w-full overflow-x-auto">
        <div className="mb-4">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            <span>{term.name}</span>
            <div className="text-sm font-normal text-gray-600 dark:text-gray-400 flex items-center gap-2 mt-1">
              <Clock className="h-4 w-4" />
              <span>Click empty slot to create • Drag to move • Hold Alt or Ctrl to copy</span>
            </div>
          </div>
        </div>

        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
          <div className="inline-block min-w-full">
            {/*
              One scroll region + sticky header so column widths match the body (scrollbar gutter
              does not shrink only the grid below the header).
            */}
            <div className="flex h-[min(70vh,720px)] min-h-[220px] flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <div
                ref={scheduleScrollRef}
                className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-y-contain [scrollbar-gutter:stable]"
              >
                <div
                  className={`${WEEKLY_GRID_CLASS} sticky top-0 z-30 shrink-0 border-b border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800`}
                >
                  <div className="sticky left-0 z-40 border-r border-gray-200 bg-gray-100 p-2 dark:border-gray-700 dark:bg-gray-800">
                    <div className="font-semibold text-gray-900 dark:text-white text-sm">Time</div>
                  </div>
                  {dayOrder.map((dayIndex) => (
                    <div
                      key={dayIndex}
                      className="border-r border-gray-200 bg-gray-100 p-2 text-center last:border-r-0 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <div className="font-semibold text-gray-900 dark:text-white text-sm">{DAYS_OF_WEEK[dayIndex].substring(0, 3)}</div>
                    </div>
                  ))}
                </div>

                <div className={`${WEEKLY_GRID_CLASS} relative`} style={{ minHeight: `${((latestTime - earliestTime) / 30) * WEEKLY_PX_PER_30_MIN}px` }}>
                <div className="border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky left-0 z-10 relative">
                  {visibleTimeSlots.map((slotMinutes, index) => {
                    const pos = ((slotMinutes - earliestTime) / (latestTime - earliestTime)) * 100;
                    return (
                      <React.Fragment key={slotMinutes}>
                        <div className="absolute left-0 right-0 border-b border-gray-200 dark:border-gray-700" style={{ top: `${pos}%` }} />
                        {index % 2 === 0 && (
                          <div className="absolute left-0 right-0 px-1 pt-0.5 text-xs text-gray-600 dark:text-gray-400" style={{ top: `${pos}%` }}>
                            {formatTime(minutesToTime(slotMinutes))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                {dayOrder.map((dayIndex) => {
                  const dayLessons = lessonsByDay[dayIndex];
                  return (
                    <DroppableDayColumn
                      key={dayIndex}
                      id={`day-${dayIndex}`}
                      dayIndex={dayIndex}
                      dropTarget={dropTarget}
                      hoverTarget={hoverTarget}
                      onTimeSlotClick={onTimeSlotClick ? (event) => handleTimeSlotClick(event, dayIndex) : undefined}
                      onTimeSlotHover={onTimeSlotClick ? (event) => handleTimeSlotHover(event, dayIndex) : undefined}
                      onTimeSlotHoverLeave={onTimeSlotClick ? handleTimeSlotHoverLeave : undefined}
                    >
                      {visibleTimeSlots.map((slotMinutes) => {
                        const pos = ((slotMinutes - earliestTime) / (latestTime - earliestTime)) * 100;
                        return (
                          <div
                            key={slotMinutes}
                            className="absolute left-0 right-0 border-b border-gray-100 dark:border-gray-800"
                            style={{ top: `${pos}%` }}
                          />
                        );
                      })}
                      {dayLessons.map((lesson) => (
                        <DraggableLessonSlot
                          key={lesson.id}
                          lesson={lesson}
                          style={getLessonStyle(lesson, dayLessons)}
                          dayLessons={dayLessons}
                          getLessonStyle={getLessonStyle}
                          isFull={isFull}
                          getAvailableSpots={getAvailableSpots}
                          getCoachName={getCoachName}
                          programNameById={programNameById}
                          onClick={() => onLessonClick?.(lesson)}
                          onDelete={onLessonDelete}
                          onCancel={onLessonCancel}
                          onStaffEnroll={onStaffEnroll}
                          lessonsOverlap={lessonsOverlap}
                          getOverlappingGroups={getOverlappingGroups}
                          hasCoachAvailabilityWarning={coachAvailabilityWarnings.get(lesson.id)?.has(dayIndex) ?? false}
                          ageGroupById={ageGroupById}
                        />
                      ))}
                    </DroppableDayColumn>
                  );
                })}
                </div>
              </div>
            </div>
          </div>

          <DragOverlay dropAnimation={null}>
            {activeLesson ? (
              <DragOverlayLesson
                lesson={activeLesson}
                programNameById={programNameById}
                ageGroupById={ageGroupById}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </TooltipProvider>
  );
}

function DroppableDayColumn({
  id,
  dayIndex,
  dropTarget,
  hoverTarget,
  onTimeSlotClick,
  onTimeSlotHover,
  onTimeSlotHoverLeave,
  children,
}: {
  id: string;
  dayIndex: number;
  dropTarget: { dayIndex: number; topPercent: number; heightPercent: number } | null;
  hoverTarget: { dayIndex: number; topPercent: number; heightPercent: number } | null;
  onTimeSlotClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onTimeSlotHover?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onTimeSlotHoverLeave?: () => void;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const showTarget = dropTarget?.dayIndex === dayIndex;
  const showHoverTarget = hoverTarget?.dayIndex === dayIndex;
  return (
    <div
      ref={setNodeRef}
      onClick={(event) => {
        if (!onTimeSlotClick) return;
        const target = event.target as HTMLElement;
        if (target.closest('[data-lesson-slot="true"]')) return;
        onTimeSlotClick(event);
      }}
      onMouseMove={(event) => onTimeSlotHover?.(event)}
      onMouseLeave={onTimeSlotHoverLeave}
      className={`border-r border-gray-200 dark:border-gray-700 last:border-r-0 relative bg-white dark:bg-gray-900 transition-colors ${isOver ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''} ${onTimeSlotClick ? 'cursor-cell' : ''}`}
    >
      {showHoverTarget && hoverTarget && (
        <div
          className="absolute left-1 right-1 rounded border border-blue-400/70 bg-blue-200/20 dark:bg-blue-500/15 pointer-events-none z-10"
          style={{
            top: `${hoverTarget.topPercent}%`,
            height: `${hoverTarget.heightPercent}%`,
            minHeight: '24px',
          }}
          aria-hidden
        />
      )}
      {showTarget && dropTarget && (
        <div
          className="absolute left-1 right-1 rounded border-2 border-dashed border-blue-500 bg-transparent pointer-events-none z-20"
          style={{
            top: `${dropTarget.topPercent}%`,
            height: `${dropTarget.heightPercent}%`,
            minHeight: '32px',
          }}
          aria-hidden
        />
      )}
      {children}
    </div>
  );
}
